// SessionContextSummaryChip — the parent agent's ACTIVE-window occupancy (rises
// during a run, resets when the next turn rebuilds a thin seed) plus the
// monotonic SESSION MEMORY retained in the context store. One row, one percent.
// Click opens the Progress dock Context footer for full details; the meaning of
// the two figures is a hover tooltip, not inline prose.

import { Tooltip } from '@localagent/ui/components/tooltip';
import type { ContextSnapshot } from '../../types/context-types';
import type { ContextFootprint } from '../../types/session-types';
import { CONTEXT_CHIP_TOOLTIP } from './context-copy';

export interface SessionContextSummaryChipProps {
  /** Parent / root context snapshot. Null before any run — the chip then
   * renders memory-only rather than vanishing. */
  snapshot: ContextSnapshot | null | undefined;
  /** Whole-session retained tokens from the context store. */
  contextFootprint?: ContextFootprint;
  onOpenDetails: () => void;
}

function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
  return String(tokens);
}

function barColor(percent: number): string {
  if (percent > 85) return 'bg-error';
  if (percent > 60) return 'bg-warning';
  return 'bg-success';
}

export function SessionContextSummaryChip({ snapshot, contextFootprint, onOpenDetails }: SessionContextSummaryChipProps) {
  const usagePercent = snapshot?.usagePercent ?? null;
  const windowSize = snapshot?.contextWindowSize ?? null;
  const promptTokens = snapshot?.promptTokens ?? null;
  const memoryTokens = contextFootprint?.total_tokens ?? null;
  const hasMemory = memoryTokens != null && memoryTokens > 0;
  const hasUsage = usagePercent != null && windowSize != null;

  // No-data rule: never fabricate 0% — with no run and no retained memory
  // there is genuinely nothing to show.
  if (!hasUsage && !hasMemory) return null;

  const percent = hasUsage ? Math.min(100, Math.max(0, usagePercent!)) : null;
  const tokensLabel =
    hasUsage && promptTokens != null
      ? `${formatTokens(promptTokens)} / ${formatTokens(windowSize!)}`
      : formatTokens(windowSize!);

  return (
    <Tooltip label={CONTEXT_CHIP_TOOLTIP} side="top">
      <button
        type="button"
        data-testid="session-context-summary"
        onClick={onOpenDetails}
        className="flex w-full items-center gap-2 rounded-md px-1 py-2 text-left transition-colors hover:bg-accent/40"
      >
        {hasUsage ? (
          <div data-testid="context-usage-bar" className="flex items-center gap-2">
            <div className="h-1.5 max-w-[120px] w-[120px] shrink-0 overflow-hidden rounded-full bg-muted">
              <div className={`h-full rounded-full ${barColor(percent!)}`} style={{ width: `${percent}%` }} />
            </div>
            <span data-testid="context-usage-percent" className="text-caption text-muted-foreground whitespace-nowrap">
              {tokensLabel} ({Math.round(percent!)}%)
            </span>
          </div>
        ) : null}
        {hasMemory && (
          <span data-testid="session-memory-tokens" className="text-caption text-muted-foreground whitespace-nowrap">
            {hasUsage ? '· ' : ''}
            memory {formatTokens(memoryTokens!)}
          </span>
        )}
      </button>
    </Tooltip>
  );
}
